import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { TestIds } from '@/constants/testIds'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { FormattedMessage, useIntl } from 'react-intl'
import { useEffect, useState } from 'react'
import { useForm, useController } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ResetPasswordSchema, type ResetPasswordFormData } from '@/schema'
import { resetPassword } from '@/services/authentication/resetPassword'
import { registrationEmailAtom } from '@/state/global'
import { Spinner } from '@iaa-di/spinner'
import { LoadingCard } from '@/components/LoadingCard'
import { RegistrationStatus } from '@/types/'
import { useAtomValue, useSetAtom } from 'jotai'
import { getRegistrationErrorTranslationKey } from '@/helpers/registration-helpers'
import { formatPhoneNumber } from '@/helpers/phone'
import { RegistrationError } from '@/types/authentication'
import { initialWorkOrderIdAtom } from '@/state/authentication/login'
import { FieldError } from '@/components/FieldError'
import { fieldInputClassName } from '@/helpers/fieldInputClassName'
import { ClarityEvent, trackClarityEvent } from '@/services/telemetry/clarity'
import { readOnlyRequestAtom } from '@/state/dashboard'

export const Route = createFileRoute('/resetPassword')({
  component: ResetPassword,
})

function ResetPassword() {
  useDocumentTitle('meta.title.resetPassword')
  const intl = useIntl()
  const navigate = useNavigate()

  const email = useAtomValue(registrationEmailAtom)
  const setInitialWorkOrderId = useSetAtom(initialWorkOrderIdAtom)
  const setReadOnlyRequest = useSetAtom(readOnlyRequestAtom)

  useEffect(() => {
    if (!email) {
      navigate({ to: '/login' })
    }
  }, [email, navigate])

  const [isError, setIsError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, _setSuccessMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(ResetPasswordSchema),
    mode: 'onTouched',
  })

  const { field: phoneField } = useController({ name: 'phone', control })

  const onSubmit = async (data: ResetPasswordFormData) => {
    setIsError(false)
    setIsLoading(true)

    const resetRequest = {
      emailId: email,
      phoneNumber: data.phone.replace(/\D/g, ''),
      action: 'passwordreset',
      password: data.password,
    }

    const response = await resetPassword(resetRequest)
    setIsLoading(false)
    if (!response.isSuccess) {
      setIsError(true)
      setErrorMessage(
        getRegistrationErrorTranslationKey(
          response?.errorMessageKey ?? RegistrationError.Unknown,
        ),
      )
    } else {
      setReadOnlyRequest(null)
      sessionStorage.setItem('isSignedIn', 'true')

      if ((response.data?.workOrderId ?? null) !== null) {
        setInitialWorkOrderId(response.data?.workOrderId ?? null)
      }

      if (
        response.registrationStatus === RegistrationStatus.RegisteredNotVerified
      ) {
        trackClarityEvent(ClarityEvent.VerificationRequired)
        navigate({ to: '/verifyInfo' })
      } else {
        if ((response.data?.workOrderId ?? null) !== null) {
          trackClarityEvent(ClarityEvent.VerificationNotRequired)
          navigate({ to: '/dashboard' })
        } else {
          navigate({ to: '/login' })
        }
      }
    }
  }

  return (
    <div className="flex items-center justify-center overflow-hidden mt-2">
      <div className="w-full max-w-md space-y-8 px-4">
        {isLoading && (
          <div
            data-testid={TestIds.resetPassword.loadingContainer}
            className="pt-32 absolute left-[47%] z-[300]"
          >
            <Spinner />
          </div>
        )}
        {isLoading && (
          <div className="pt-16">
            <LoadingCard />
          </div>
        )}
        {!isLoading && (
          <>
            <h2 className="text-2xl font-bold">
              <FormattedMessage id="login.completeResetPasswordForm" />
            </h2>
            <p className="text-sm text-gray-600">{email}</p>
            <hr className="border-0 h-2 w-1/3 bg-red-600" />
            {!successMessage && (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium">
                    <FormattedMessage id="login.phone" />
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    data-testid={TestIds.resetPassword.phoneInput}
                    value={phoneField.value ?? ''}
                    onChange={(e) =>
                      phoneField.onChange(formatPhoneNumber(e.target.value))
                    }
                    onBlur={phoneField.onBlur}
                    placeholder={intl.formatMessage({ id: 'login.phone' })}
                    className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none ${fieldInputClassName(!!errors.phone)}`}
                  />
                  <FieldError
                    testId={TestIds.resetPassword.phoneError}
                    messageId={errors.phone?.message}
                  />
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium"
                  >
                    <FormattedMessage id="login.newPassword" />
                  </label>
                  <input
                    id="password"
                    type="password"
                    maxLength={30}
                    data-testid={TestIds.resetPassword.passwordInput}
                    {...register('password')}
                    placeholder={intl.formatMessage({
                      id: 'login.newPassword',
                    })}
                    className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none ${fieldInputClassName(!!errors.password)}`}
                  />
                  <FieldError
                    testId={TestIds.resetPassword.passwordError}
                    messageId={errors.password?.message}
                  />
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium"
                  >
                    <FormattedMessage id="login.confirmPassword" />
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    maxLength={30}
                    data-testid={TestIds.resetPassword.confirmPasswordInput}
                    {...register('confirmPassword')}
                    placeholder={intl.formatMessage({
                      id: 'login.confirmPassword',
                    })}
                    className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none ${fieldInputClassName(!!errors.confirmPassword)}`}
                  />
                  <FieldError
                    testId={TestIds.resetPassword.confirmPasswordError}
                    messageId={errors.confirmPassword?.message}
                  />
                </div>

                {isError && (
                  <div
                    data-testid={TestIds.resetPassword.errorMessage}
                    role="alert"
                    className="rounded bg-orange-500 p-4 text-sm font-semibold text-white"
                  >
                    <FormattedMessage id={errorMessage} />
                  </div>
                )}

                <button
                  type="submit"
                  data-testid={TestIds.resetPassword.submitButton}
                  disabled={isLoading || !isValid}
                  className="w-full rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FormattedMessage id="login.submit" />
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  )
}
